/**
 * 4-step wizard for creating a new feed source.
 *
 *   1. Basics        — slug (regex-validated), name, tenant, connector, format
 *   2. Connector     — dynamic form from connector.configSchema + credentialRef
 *   3. Format        — dynamic form from format.configSchema
 *   4. Schedule/trust — scheduleSeconds, weight, thresholds
 *
 * On create, the parent navigates to /feeds/$slug (the wizard banner on the
 * detail page then drives the remaining ① fetch → ② preview → ③ map → ④
 * promote flow).
 */

import { useEffect, useMemo, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { ChevronLeft, ChevronRight, Loader2, Plus, X } from "lucide-react";
import { toast } from "sonner";

import { ApiError } from "~/lib/api";
import {
    type CreateFeedInput,
    type ConnectorDescriptor,
    type FormatDescriptor,
    useConnectors,
    useCreateFeedSource,
    useFormats,
} from "~/lib/feeds";
import { SchemaForm, schemaDefaults } from "./SchemaForm";

const SLUG_RE = /^[a-z0-9][a-z0-9-]{1,63}$/;

function slugify(name: string): string {
    return name
        .toLowerCase()
        .normalize("NFKD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "")
        .slice(0, 64);
}

interface Props {
    onCreated?: (slug: string) => void;
    triggerClassName?: string;
}

export function CreateFeedDialog({ onCreated, triggerClassName }: Props): React.ReactElement {
    const [open, setOpen] = useState(false);
    const [step, setStep] = useState(1);

    const [name, setName] = useState("");
    const [slug, setSlug] = useState("");
    const [slugDirty, setSlugDirty] = useState(false);
    const [connectorKind, setConnectorKind] = useState("");
    const [formatKind, setFormatKind] = useState("");
    const [tenantId, setTenantId] = useState("");
    const [credentialRef, setCredentialRef] = useState("");
    const [connectorCfg, setConnectorCfg] = useState<Record<string, unknown>>({});
    const [formatCfg, setFormatCfg] = useState<Record<string, unknown>>({});

    const [scheduleSeconds, setScheduleSeconds] = useState(3600);
    const [weight, setWeight] = useState(50);
    const [circuitBreakerThreshold, setCircuitBreakerThreshold] = useState(20);
    const [ingestWorkerConcurrency, setIngestWorkerConcurrency] = useState(4);
    const [sampleRetentionDays, setSampleRetentionDays] = useState(30);

    const [error, setError] = useState<string | null>(null);

    const connectors = useConnectors();
    const formats = useFormats();
    const create = useCreateFeedSource();

    const selectedConnector: ConnectorDescriptor | undefined = useMemo(
        () => connectors.data?.find((c) => c.kind === connectorKind),
        [connectors.data, connectorKind],
    );
    const selectedFormat: FormatDescriptor | undefined = useMemo(
        () => formats.data?.find((f) => f.kind === formatKind),
        [formats.data, formatKind],
    );

    // Seed connector/format config defaults when selection changes.
    useEffect(() => {
        if (selectedConnector) setConnectorCfg(schemaDefaults(selectedConnector.configSchema));
    }, [selectedConnector]);
    useEffect(() => {
        if (selectedFormat) setFormatCfg(schemaDefaults(selectedFormat.configSchema));
    }, [selectedFormat]);

    function reset(): void {
        setStep(1);
        setName("");
        setSlug("");
        setSlugDirty(false);
        setConnectorKind("");
        setFormatKind("");
        setTenantId("");
        setCredentialRef("");
        setConnectorCfg({});
        setFormatCfg({});
        setScheduleSeconds(3600);
        setWeight(50);
        setCircuitBreakerThreshold(20);
        setIngestWorkerConcurrency(4);
        setSampleRetentionDays(30);
        setError(null);
        create.reset();
    }

    function canAdvance(): boolean {
        if (step === 1) {
            return !!name.trim() && SLUG_RE.test(slug) && !!connectorKind && !!formatKind;
        }
        if (step === 2) {
            if (selectedConnector?.requiresCredential && !credentialRef.trim()) return false;
            return true;
        }
        if (step === 3) return true;
        return true;
    }

    async function handleCreate(): Promise<void> {
        setError(null);
        if (!SLUG_RE.test(slug)) {
            setError("Slug must match [a-z0-9][a-z0-9-]{1,63}.");
            return;
        }
        const input: CreateFeedInput = {
            slug,
            name: name.trim(),
            connectorKind,
            formatKind,
            config: { ...connectorCfg, ...(Object.keys(formatCfg).length ? { format: formatCfg } : {}) },
            credentialRef: credentialRef.trim() || undefined,
            scheduleSeconds,
            weight,
            circuitBreakerThreshold,
            ingestWorkerConcurrency,
            sampleRetentionDays,
            tenantId: tenantId.trim() || undefined,
        };
        try {
            const res = await create.mutateAsync(input);
            toast.success(`Feed “${name.trim()}” created`);
            setOpen(false);
            reset();
            onCreated?.(res.slug);
        } catch (err) {
            const msg = err instanceof ApiError ? err.message : "Failed to create feed.";
            setError(msg);
            toast.error(msg);
        }
    }

    return (
        <Dialog.Root
            open={open}
            onOpenChange={(o) => {
                setOpen(o);
                if (!o) reset();
            }}
        >
            <Dialog.Trigger asChild>
                <button type="button" className={triggerClassName ?? "btn btn--primary press-crush"}>
                    <Plus size={14} />
                    <span>New feed</span>
                </button>
            </Dialog.Trigger>
            <Dialog.Portal>
                <Dialog.Overlay className="dialog-overlay">
                    <Dialog.Content
                        className="dialog-content enter-up"
                        style={{ padding: "var(--space-6)", maxWidth: 640, width: "min(640px, 100vw)" }}
                        onOpenAutoFocus={(e) => {
                            // Focus first real input, not the close button.
                            const el = (e.currentTarget as HTMLElement).querySelector<HTMLInputElement>("input");
                            if (el) {
                                e.preventDefault();
                                el.focus();
                            }
                        }}
                    >
                        <div className="cluster-xs" style={{ justifyContent: "space-between" }}>
                            <Dialog.Title className="type-h2">New feed</Dialog.Title>
                            <Dialog.Close asChild>
                                <button type="button" className="btn btn--ghost btn--sm" aria-label="Close">
                                    <X size={14} />
                                </button>
                            </Dialog.Close>
                        </div>

                        <Stepper step={step} />

                        <div className="stack" style={{ marginTop: "var(--space-4)" }}>
                            {step === 1 && (
                                <Step1Basics
                                    name={name}
                                    setName={(v) => {
                                        setName(v);
                                        if (!slugDirty) setSlug(slugify(v));
                                    }}
                                    slug={slug}
                                    setSlug={(v) => {
                                        setSlug(v);
                                        setSlugDirty(true);
                                    }}
                                    tenantId={tenantId}
                                    setTenantId={setTenantId}
                                    connectorKind={connectorKind}
                                    setConnectorKind={setConnectorKind}
                                    formatKind={formatKind}
                                    setFormatKind={setFormatKind}
                                    connectors={connectors.data ?? []}
                                    formats={formats.data ?? []}
                                />
                            )}
                            {step === 2 && (
                                <Step2Connector
                                    connector={selectedConnector}
                                    config={connectorCfg}
                                    onConfig={setConnectorCfg}
                                    credentialRef={credentialRef}
                                    setCredentialRef={setCredentialRef}
                                />
                            )}
                            {step === 3 && (
                                <Step3Format
                                    format={selectedFormat}
                                    config={formatCfg}
                                    onConfig={setFormatCfg}
                                />
                            )}
                            {step === 4 && (
                                <Step4Schedule
                                    scheduleSeconds={scheduleSeconds}
                                    setScheduleSeconds={setScheduleSeconds}
                                    weight={weight}
                                    setWeight={setWeight}
                                    circuitBreakerThreshold={circuitBreakerThreshold}
                                    setCircuitBreakerThreshold={setCircuitBreakerThreshold}
                                    ingestWorkerConcurrency={ingestWorkerConcurrency}
                                    setIngestWorkerConcurrency={setIngestWorkerConcurrency}
                                    sampleRetentionDays={sampleRetentionDays}
                                    setSampleRetentionDays={setSampleRetentionDays}
                                />
                            )}

                            {error && (
                                <div
                                    className="chip chip--danger shake"
                                    style={{ height: "auto", padding: "var(--space-2) var(--space-3)" }}
                                    role="alert"
                                >
                                    {error}
                                </div>
                            )}

                            <div className="cluster-xs" style={{ justifyContent: "space-between", marginTop: "var(--space-3)" }}>
                                <button
                                    type="button"
                                    className="btn btn--ghost"
                                    disabled={step === 1}
                                    onClick={() => setStep((s) => Math.max(1, s - 1))}
                                >
                                    <ChevronLeft size={14} />
                                    Back
                                </button>
                                {step < 4 && (
                                    <button
                                        type="button"
                                        className="btn btn--primary press-crush"
                                        disabled={!canAdvance()}
                                        onClick={() => setStep((s) => Math.min(4, s + 1))}
                                    >
                                        Next
                                        <ChevronRight size={14} />
                                    </button>
                                )}
                                {step === 4 && (
                                    <button
                                        type="button"
                                        className="btn btn--primary press-crush"
                                        disabled={create.isPending || !canAdvance()}
                                        onClick={() => void handleCreate()}
                                    >
                                        {create.isPending ? (
                                            <Loader2 size={14} className="animate-spin" />
                                        ) : (
                                            <Plus size={14} />
                                        )}
                                        <span>{create.isPending ? "Creating…" : "Create feed"}</span>
                                    </button>
                                )}
                            </div>
                        </div>
                    </Dialog.Content>
                </Dialog.Overlay>
            </Dialog.Portal>
        </Dialog.Root>
    );
}

function Stepper({ step }: { step: number }): React.ReactElement {
    const labels = ["Basics", "Connector", "Format", "Schedule"];
    return (
        <ol
            className="cluster-xs"
            style={{
                margin: "var(--space-4) 0 var(--space-2)",
                gap: "var(--space-2)",
                listStyle: "none",
                padding: 0,
            }}
            aria-label="Wizard progress"
        >
            {labels.map((label, i) => {
                const n = i + 1;
                const done = n < step;
                const current = n === step;
                return (
                    <li
                        key={label}
                        className="cluster-xs text-12"
                        style={{
                            gap: "var(--space-2)",
                            color: done
                                ? "var(--color-success)"
                                : current
                                  ? "var(--color-text)"
                                  : "var(--color-text-subtle)",
                        }}
                        aria-current={current ? "step" : undefined}
                    >
                        <span
                            className="tabular-nums"
                            style={{
                                width: 20,
                                height: 20,
                                borderRadius: "50%",
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                background: current
                                    ? "var(--color-accent-soft)"
                                    : done
                                      ? "var(--color-success-soft)"
                                      : "var(--color-surface-sunken)",
                                fontWeight: "var(--font-weight-semibold)",
                                fontSize: "var(--font-size-11)",
                            }}
                        >
                            {n}
                        </span>
                        {label}
                    </li>
                );
            })}
        </ol>
    );
}

function Step1Basics(props: {
    name: string;
    setName: (v: string) => void;
    slug: string;
    setSlug: (v: string) => void;
    tenantId: string;
    setTenantId: (v: string) => void;
    connectorKind: string;
    setConnectorKind: (v: string) => void;
    formatKind: string;
    setFormatKind: (v: string) => void;
    connectors: ConnectorDescriptor[];
    formats: FormatDescriptor[];
}): React.ReactElement {
    return (
        <div className="stack-sm">
            <label className="stack-xs">
                <span className="text-12 text-text-muted">Name</span>
                <input
                    className="input"
                    value={props.name}
                    onChange={(e) => props.setName(e.target.value)}
                    placeholder="URLhaus abuse.ch"
                    autoFocus
                />
            </label>
            <label className="stack-xs">
                <span className="text-12 text-text-muted">
                    Slug <span className="text-text-subtle">(URL-safe, lowercase)</span>
                </span>
                <input
                    className="input"
                    value={props.slug}
                    onChange={(e) => props.setSlug(e.target.value)}
                    pattern="[a-z0-9][a-z0-9-]{1,63}"
                    style={{ fontFamily: "var(--font-family-mono)" }}
                />
            </label>
            <label className="stack-xs">
                <span className="text-12 text-text-muted">
                    Tenant ID <span className="text-text-subtle">(optional — leave blank for platform-wide)</span>
                </span>
                <input
                    className="input"
                    value={props.tenantId}
                    onChange={(e) => props.setTenantId(e.target.value)}
                    style={{ fontFamily: "var(--font-family-mono)" }}
                    placeholder="uuid"
                />
            </label>
            <label className="stack-xs">
                <span className="text-12 text-text-muted">Connector</span>
                <select
                    className="input"
                    value={props.connectorKind}
                    onChange={(e) => props.setConnectorKind(e.target.value)}
                >
                    <option value="">— select —</option>
                    {props.connectors.map((c) => (
                        <option key={c.kind} value={c.kind}>
                            {c.displayName} — {c.kind}
                        </option>
                    ))}
                </select>
            </label>
            <label className="stack-xs">
                <span className="text-12 text-text-muted">Format</span>
                <select
                    className="input"
                    value={props.formatKind}
                    onChange={(e) => props.setFormatKind(e.target.value)}
                >
                    <option value="">— select —</option>
                    {props.formats.map((f) => (
                        <option key={f.kind} value={f.kind}>
                            {f.displayName} — {f.kind}
                        </option>
                    ))}
                </select>
            </label>
        </div>
    );
}

function Step2Connector(props: {
    connector?: ConnectorDescriptor;
    config: Record<string, unknown>;
    onConfig: (c: Record<string, unknown>) => void;
    credentialRef: string;
    setCredentialRef: (v: string) => void;
}): React.ReactElement {
    if (!props.connector) {
        return <div className="text-12 text-text-muted">Select a connector first.</div>;
    }
    return (
        <div className="stack-sm">
            <div className="text-13 text-text-muted">{props.connector.description}</div>
            <SchemaForm
                schema={props.connector.configSchema}
                value={props.config}
                onChange={props.onConfig}
            />
            {props.connector.requiresCredential && (
                <label className="stack-xs">
                    <span className="text-12 text-text-muted">
                        Credential ref
                        <span className="text-text-subtle" style={{ marginLeft: 6 }}>
                            env:VAR_NAME or vault://path
                        </span>
                    </span>
                    <input
                        className="input"
                        value={props.credentialRef}
                        onChange={(e) => props.setCredentialRef(e.target.value)}
                        placeholder="env:OTX_API_KEY"
                        style={{ fontFamily: "var(--font-family-mono)" }}
                        aria-required
                    />
                </label>
            )}
        </div>
    );
}

function Step3Format(props: {
    format?: FormatDescriptor;
    config: Record<string, unknown>;
    onConfig: (c: Record<string, unknown>) => void;
}): React.ReactElement {
    if (!props.format) {
        return <div className="text-12 text-text-muted">Select a format first.</div>;
    }
    return (
        <div className="stack-sm">
            <div className="text-13 text-text-muted">{props.format.description}</div>
            <SchemaForm
                schema={props.format.configSchema}
                value={props.config}
                onChange={props.onConfig}
            />
        </div>
    );
}

const SCHEDULE_PRESETS: Array<{ secs: number; label: string }> = [
    { secs: 300, label: "every 5m" },
    { secs: 900, label: "every 15m" },
    { secs: 1800, label: "every 30m" },
    { secs: 3600, label: "every 1h" },
    { secs: 10800, label: "every 3h" },
    { secs: 21600, label: "every 6h" },
    { secs: 43200, label: "every 12h" },
    { secs: 86400, label: "every 24h" },
];

function Step4Schedule(props: {
    scheduleSeconds: number;
    setScheduleSeconds: (n: number) => void;
    weight: number;
    setWeight: (n: number) => void;
    circuitBreakerThreshold: number;
    setCircuitBreakerThreshold: (n: number) => void;
    ingestWorkerConcurrency: number;
    setIngestWorkerConcurrency: (n: number) => void;
    sampleRetentionDays: number;
    setSampleRetentionDays: (n: number) => void;
}): React.ReactElement {
    return (
        <div className="stack-sm">
            <label className="stack-xs">
                <span className="text-12 text-text-muted">Schedule</span>
                <select
                    className="input"
                    value={props.scheduleSeconds}
                    onChange={(e) => props.setScheduleSeconds(Number(e.target.value))}
                >
                    {SCHEDULE_PRESETS.map((p) => (
                        <option key={p.secs} value={p.secs}>
                            {p.label}
                        </option>
                    ))}
                </select>
            </label>
            <label className="stack-xs">
                <span className="text-12 text-text-muted tabular-nums">
                    Trust weight: {props.weight}
                </span>
                <input
                    type="range"
                    min={0}
                    max={100}
                    value={props.weight}
                    onChange={(e) => props.setWeight(Number(e.target.value))}
                    aria-valuenow={props.weight}
                    aria-valuemin={0}
                    aria-valuemax={100}
                />
            </label>
            <label className="stack-xs">
                <span className="text-12 text-text-muted tabular-nums">
                    Worker concurrency: {props.ingestWorkerConcurrency}
                </span>
                <input
                    type="range"
                    min={1}
                    max={16}
                    value={props.ingestWorkerConcurrency}
                    onChange={(e) => props.setIngestWorkerConcurrency(Number(e.target.value))}
                    aria-valuenow={props.ingestWorkerConcurrency}
                    aria-valuemin={1}
                    aria-valuemax={16}
                />
            </label>
            <label className="stack-xs">
                <span className="text-12 text-text-muted">Circuit breaker threshold</span>
                <input
                    type="number"
                    className="input tabular-nums"
                    value={props.circuitBreakerThreshold}
                    min={1}
                    max={500}
                    onChange={(e) => props.setCircuitBreakerThreshold(Number(e.target.value) || 20)}
                />
            </label>
            <label className="stack-xs">
                <span className="text-12 text-text-muted">Sample retention (days)</span>
                <input
                    type="number"
                    className="input tabular-nums"
                    value={props.sampleRetentionDays}
                    min={1}
                    max={365}
                    onChange={(e) => props.setSampleRetentionDays(Number(e.target.value) || 30)}
                />
            </label>
        </div>
    );
}
